import { GTMFlowSteps, GTMTimeData, GTMTimeElapse } from './../interfaces/GTM';
import { LoanData } from '../interfaces/Debt';
import { PaymentCard } from '../interfaces/Loan';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { PaymentMethod } from '../interfaces/PaymentMethod';
import { PaymentPlanStatus } from '../interfaces/PaymentPlan';
import { dateFormatStandard } from './dateTimeHelper';

export const getCustAgeSegment = (birthday: string) => {
  // (18-25, 26-35, 36-45, 46-55, 56-65, 66-75, 75+)
  let ageSegmentsArray = [25, 35, 45, 55, 65, 75];
  let age = Math.floor(
    (new Date().getTime() - new Date(birthday).getTime()) /
      (1000 * 60 * 60 * 24 * 365.25)
  );
  let segment = 0;
  let ageSegment = '';
  for (let i = 0; i < ageSegmentsArray.length; i++) {
    if (ageSegmentsArray[i] >= age) {
      segment = i + 1;
      break;
    } else if (ageSegmentsArray.length - 1 === i && segment === 0) {
      segment = 7;
    }
  }

  switch (segment) {
    case 1:
      ageSegment = '18-25';
      break;
    case 2:
      ageSegment = '26-35';
      break;
    case 3:
      ageSegment = '36-45';
      break;
    case 4:
      ageSegment = '46-55';
      break;
    case 5:
      ageSegment = '56-65';
      break;
    case 6:
      ageSegment = '66-75';
      break;
    case 7:
      ageSegment = '75+';
      break;
  }
  return ageSegment;
};

export const getBirthYear = (birthday: string) => {
  return new Date(birthday).getFullYear().toString();
};

export const getEmailDomain = (email: string) => {
  return /@/.exec(email) ? email.split('@')[1] : '';
};

export const checkPhones = (phones: string[]) => {
  let check = false;
  phones.forEach((phone) => {
    if (/^[67](?:\d){8}$/.test(phone)) check = true;
  });
  return check;
};

export const getLoanCount = (array: LoanData[], type: string) => {
  let count = 0;
  array.forEach((element) => {
    if (element.product_type === type) count++;
  });
  return count.toString();
};

export const getCardData = (array: PaymentCard[]): string => {
  let response = '';
  array.forEach((element) => {
    for (let value of Object.keys(element)) {
      //Falta añadir de alguna manera el element.payment_method_id
      if (value === 'type') {
        response = value;
        break;
      }
      if (value === 'iin_number') {
        response = value;
        break;
      }
    }
  });
  return response;
};

export const getFlowStep = (pathname: string): GTMFlowSteps => {
  let flowStep;
  switch (pathname) {
    case '/':
      flowStep = GTMFlowSteps.ENTRY;
      break;
    case '/entry-co':
      flowStep = GTMFlowSteps.ENTRY_CO;
      break;
    case '/types-payments':
      flowStep = GTMFlowSteps.TYPES_PAYMENTS;
      break;
    case '/tpv':
      flowStep = GTMFlowSteps.TPV;
      break;
    case '/transition':
      flowStep = GTMFlowSteps.TRANSITION;
      break;
    case '/timeout':
      flowStep = GTMFlowSteps.TIMEOUT;
      break;
    case '/denial':
      flowStep = GTMFlowSteps.DENIAL;
      break;
    case '/unknown-error':
      flowStep = GTMFlowSteps.UNKNOWN_ERROR;
      break;
    case '/unprocessable':
      flowStep = GTMFlowSteps.UNPROCESSABLE;
      break;
    case '/success':
      flowStep = GTMFlowSteps.SUCCESS;
      break;
    case '/payment-check':
      flowStep = GTMFlowSteps.PAYMENT_CHECK;
      break;
    case '/no-debt':
      flowStep = GTMFlowSteps.NO_DEBT;
      break;
    default:
      flowStep = GTMFlowSteps.ENTRY;
  }
  return flowStep;
};

dayjs.extend(duration);

export const getTimeElapse = (timeData: GTMTimeElapse): number => {
  const startTime = dayjs(timeData.initTime);
  const finishTime = dayjs(timeData.finishTime);

  const diffInSeconds = dayjs.duration(finishTime.diff(startTime)).asSeconds();
  return diffInSeconds;
};

export const getTimeResult = (
  initTime: GTMTimeElapse['initTime']
): GTMTimeData => {
  const timeDataObj: GTMTimeElapse = {
    initTime: initTime,
    finishTime: dayjs(),
  };

  const elapseD = getTimeElapse(timeDataObj);

  const timeElapsedData: GTMTimeData = {
    time_elapsed: elapseD,
  };
  return timeElapsedData;
};

export const recoverPaymentMethod = () => {
  let savedPaymentMethod =
    sessionStorage.getItem('pepper-payment-method') ?? '';
  savedPaymentMethod =
    savedPaymentMethod === PaymentMethod.GOOGLE ||
    savedPaymentMethod === PaymentMethod.APPLE
      ? 'WALLET'
      : savedPaymentMethod === PaymentMethod.EXPRESS
      ? 'REUSE_CARD'
      : savedPaymentMethod;
  return savedPaymentMethod;
};

// Operaciones del cliente con planes de pago
export const getPaymentPlanCount = (array: LoanData[]): string => {
  let count = 0;
  array.forEach((element) => {
    if (element.allow_creation_payment_plan) count++;
  });
  return count.toString();
};

// Este método recibe todas las opeaciones y devuelve el número total que permiten planes pag
export const getPaymentPlanStatesCount = (array: LoanData[], status: PaymentPlanStatus): string => {
  let count = 0;
  array.forEach((element) => {
    const paymentPlanData = element.payment_plan;
    if (paymentPlanData && paymentPlanData.length > 0) {
      paymentPlanData.forEach((plan) => {
        if (plan.state === status) {
          count++;
        }
      });
    }
  });
  return count.toString();
};

// Obtenemos el día mínimo de inicio del plan de pagos
export const getMinStartDay = (array: LoanData[]): string => {
  let minDay: Date | null = null;
  array.forEach((element) => {
    const paymentPlanData = element.payment_plan;
    if (paymentPlanData && paymentPlanData.length > 0) {
      paymentPlanData.forEach((plan) => {
        if (plan.start_date) {
          const planDate = new Date(plan.start_date);
          if (!minDay || planDate < minDay) {
            minDay = planDate;
          }
        }
      });
    }
  });
  return minDay ? dateFormatStandard(minDay, true) : '';
};

// Obtenemos el día máximo de finalización del plan de pagos
export const getMaxEndDay = (array: LoanData[]): string => {
  let maxDay: Date | null = null;
  array.forEach((element) => {
    const paymentPlanData = element.payment_plan;
    if (paymentPlanData && paymentPlanData.length > 0) {
      paymentPlanData.forEach((plan) => {
        if (plan.end_date) {
          const planDate = new Date(plan.end_date);
          if (!maxDay || planDate > maxDay) {
            maxDay = planDate;
          }
        }
      });
    }
  });
  return maxDay ? dateFormatStandard(maxDay, true) : '';
};